"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateNonSpecificActionDataFromId = exports.getNonSpecificActionDataFromId = exports.prettyPrintActionId = exports.createInnerActionId = exports.buildSeqListOfActionCalls = exports._convertToDoubleAdjacencyList = exports.topologicalSort = exports.getInDegrees = exports._checkThatAllHitInActionGroup = void 0;
const interfaces_1 = require("./interfaces");
const object_1 = require("./utils/object");
const _checkThatAllHitInActionGroup = (group) => {
    const ids = Object.keys(group.actions);
    const hit = ids.map((_) => false);
    const frontier = [group.first];
    while (frontier.length !== 0) {
        const elem = frontier.pop();
        const elemIdx = ids.indexOf(elem);
        hit[elemIdx] = true;
        for (const neighborId in (0, object_1.flattenArrObject)(group.actions[elem].nextActions)) {
            const neighborIdx = ids.indexOf(neighborId);
            // If the neighborIdx is negative 1, then it is referencing an action outside
            // the current group
            if (neighborIdx !== -1 && !hit[neighborIdx]) {
                frontier.push(neighborId);
            }
        }
    }
    return hit.every((v) => v === true);
};
exports._checkThatAllHitInActionGroup = _checkThatAllHitInActionGroup;
const getInDegrees = (graph) => {
    const inDegrees = Array(graph.length).fill(0);
    graph.forEach((vertex) => vertex.forEach((vertexNexts) => vertexNexts.forEach((v) => (inDegrees[v] += 1))));
    return inDegrees;
};
exports.getInDegrees = getInDegrees;
/**
 * Get a sort on the graph. This should be DFS based as we want
 * to go down "legs" of the tree 1 at a time
 *
 * The algorithm works by using DFS and only following the dfs if
 * the amount of times a node is hit equals its in degree
 */
const topologicalSort = (graph, inDegrees, init) => {
    const hitCount = Array(graph.length).fill(0);
    // Ensure that the initial in the frontier do not have other in degrees
    const frontier = [...init.filter((idx) => inDegrees[idx] === hitCount[idx])];
    frontier.map((i) => (hitCount[i] += 1));
    const inFrontier = Array(graph.length).fill(false);
    return topologicalSortRecurse(graph, hitCount, frontier, inDegrees, inFrontier);
};
exports.topologicalSort = topologicalSort;
const topologicalSortRecurse = (graph, hitCount, frontier, inDegrees, inFrontier) => {
    const ret = [];
    while (frontier.length !== 0) {
        const idx = frontier.pop();
        // Conditionally add to the frontier
        graph[idx].forEach((nextNodes) => nextNodes.forEach((next) => {
            hitCount[next] += 1;
            if (hitCount[next] === inDegrees[next] && !inFrontier[next]) {
                frontier.push(next);
                inFrontier[next] = true;
            }
        }));
        ret.push(idx, ...topologicalSortRecurse(graph, hitCount, frontier, inDegrees, inFrontier));
    }
    return ret;
};
/**
 * Convert to an adjacency list where every next action edge pointing
 * to outside of the action group is thrown out
 */
const _convertActionGroupToInternalDoubleAdjacency = (group, actionGroupIds) => {
    const nextActions = actionGroupIds.map((id) => group.actions[id].nextActions.map((nObj) => Object.keys(nObj)));
    return nextActions.map((outerNexts) => outerNexts.map((nexts) => nexts.map((id) => actionGroupIds.indexOf(id)).filter((idx) => idx !== -1)));
};
/**
 * Convert a set of action datas to an adjacency list
 */
const _convertToDoubleAdjacencyList = (actionDatasFormatted, actionIdxToId, initActions) => {
    const newActionDatas = (0, object_1.deepCloneObject)(actionDatasFormatted);
    const actionIds = actionIdxToId;
    actionIds.forEach((id) => {
        if ((0, interfaces_1.isNonSpecificActionGroup)(actionDatasFormatted[id])) {
            const group = newActionDatas[id]
                .actionGroup;
            if (!(0, exports._checkThatAllHitInActionGroup)(group)) {
                throw `Expected no actions to be left uncalled`;
            }
            const groupActionIds = Object.keys(group.actions);
            const nextActionsMap = groupActionIds
                .map((id) => group.actions[id].nextActions)
                .flat();
            newActionDatas[id].nextActions = nextActionsMap.map((nextActionsObj) => Object.keys(nextActionsObj)
                // Only look at the actions which are found in the external actions
                .map((k) => actionIds.indexOf(k))
                .filter((idx) => idx !== -1));
        }
        else {
            const action = newActionDatas[id].action;
            newActionDatas[id].nextActions = action.nextActions.map((nextActionsObj) => Object.keys(nextActionsObj).map((k) => {
                const idx = actionIds.indexOf(k);
                if (idx === -1)
                    throw `There is an unexpected next action of id ${k} for action group ${id}`;
                return idx;
            }));
        }
    });
    const graph = actionIds.map((actionId) => newActionDatas[actionId].nextActions);
    const _initActions = initActions.map((id) => actionIds.indexOf(id));
    return {
        graph,
        initActions: _initActions,
    };
};
exports._convertToDoubleAdjacencyList = _convertToDoubleAdjacencyList;
/**
 * Take in a non specific construction and format all action id's within a group to match the @code{createInnerActionId}
 */
const formatActionIds = (actions) => {
    // TODO: this may be too slow...
    const actionsNew = (0, object_1.deepCloneObject)(actions);
    const oldOuterIds = Object.keys(actionsNew);
    for (let i = 0; i < oldOuterIds.length; i++) {
        const outerAction = oldOuterIds[i];
        // get the group new action ids
        if ((0, interfaces_1.isNonSpecificActionGroup)(actionsNew[outerAction])) {
            const group = (0, interfaces_1.castToActionGroup)(actionsNew[outerAction]);
            const oldInnerActionIds = Object.keys(group.actions);
            for (let i = 0; i < oldInnerActionIds.length; i++) {
                const innerAction = oldInnerActionIds[i];
                const newNexts = group.actions[innerAction].nextActions.map((nextActions) => {
                    const newNext = Object.assign({}, nextActions);
                    for (const next in nextActions) {
                        if (oldInnerActionIds.includes(next)) {
                            newNext[(0, exports.createInnerActionId)(outerAction, next)] = newNext[next];
                            delete newNext[next];
                        }
                    }
                    return newNext;
                });
                group.actions[(0, exports.createInnerActionId)(outerAction, innerAction)] =
                    group.actions[innerAction];
                group.actions[(0, exports.createInnerActionId)(outerAction, innerAction)].nextActions = newNexts;
                delete group.actions[innerAction];
            }
            group.first = (0, exports.createInnerActionId)(outerAction, group.first);
            actionsNew[outerAction] = { actionGroup: group };
        }
    }
    return actionsNew;
};
const buildSeqListOfActionCallsNonFlat = (actionDatas, initActions) => {
    const actionIds = Object.keys(actionDatas);
    const { graph, initActions: initIndices } = (0, exports._convertToDoubleAdjacencyList)(actionDatas, actionIds, initActions);
    if (graph.length !== actionIds.length)
        throw `Expected no isolated actions`;
    const inDegreesOuter = (0, exports.getInDegrees)(graph);
    const outerOrder = (0, exports.topologicalSort)(graph, inDegreesOuter, initIndices);
    const inDegrees2D = Array(inDegreesOuter.length).fill([]);
    // Now use the outer order to get inner order for action groups
    const orderMapped = outerOrder.map((actionIdx) => {
        if ((0, interfaces_1.isNonSpecificActionGroup)(actionDatas[actionIds[actionIdx]])) {
            const group = (0, interfaces_1.castToActionGroup)(actionDatas[actionIds[actionIdx]]);
            const actionGroupIds = Object.keys(group.actions);
            const actionGroupGraph = _convertActionGroupToInternalDoubleAdjacency(group, actionGroupIds);
            const actionInDegrees = (0, exports.getInDegrees)(actionGroupGraph);
            // Add the in degrees coming into the group for the first element
            const firstIdx = actionGroupIds.indexOf(group.first);
            if (firstIdx === -1)
                throw `Expected the first id in group ${actionIds[actionIdx]} to be an action in the group`;
            actionInDegrees[firstIdx] += inDegreesOuter[actionIdx];
            inDegrees2D[actionIdx] = actionInDegrees;
            return {
                order: [
                    actionIdx,
                    (0, exports.topologicalSort)(actionGroupGraph, actionInDegrees, [
                        actionGroupIds.indexOf(group.first),
                    ]),
                ],
            };
        }
        inDegrees2D[actionIdx] = [inDegreesOuter[actionIdx]];
        return {
            order: [actionIdx, []],
        };
    });
    const order = orderMapped.map((o) => o.order);
    // const inDegrees2D = orderMapped.map((o) => o.inDegrees);
    const actionIdxToInnerId = actionIds.map((id) => (0, interfaces_1.isNonSpecificActionGroup)(actionDatas[id])
        ? Object.keys((0, interfaces_1.castToActionGroup)(actionDatas[id]).actions).map((innerActionId) => innerActionId)
        : [id]);
    return {
        order,
        actionIdxToInnerId,
        actionIdxToOuterId: actionIds,
        initialActionIdxs: initIndices,
        inDegrees: inDegrees2D,
    };
};
/**
 * Return a list of the hit order where the outer list corresponds to
 * the order of transactions and the inner list corresponds to the actions
 * grouped into an instruction
 *
 * Every action idx is in reference to 1 FLAT list of actions. This algorithm works
 * on the assumption that every vertex is only every visited once and that the input graph is a DAG
 *
 *
 * @return actionIdxToId - a list of strings where the string at id index i corresponds to action of the same id
 * @return order - The order hit with the outer group being for action/ action group and the inner one being the order for internal actions. Order maps from
 * call order index -> action index
 * @return initActionIdxs - the initial action index's which get hit
 *
 * NOTE: this returns an unknown result for cyclic graphs
 */
const buildSeqListOfActionCalls = (actionDatas, initActions, numberOfInputTokens) => {
    const actionDatasFormattedIds = formatActionIds(actionDatas);
    const { order, actionIdxToInnerId, actionIdxToOuterId, initialActionIdxs, inDegrees, } = buildSeqListOfActionCallsNonFlat(actionDatasFormattedIds, initActions);
    const { orderFlat, actionIdxToIdFlat, initActionIdxsFlat, inDegreesFlat } = flattenOrder(order, actionIdxToInnerId, actionIdxToOuterId, initialActionIdxs, inDegrees, actionDatasFormattedIds);
    const groupingLengths = order.map((accessIdx) => accessIdxIsGroup(accessIdx) ? accessIdx[1].length : 1);
    const orderByGroups = groupingLengths.map((len) => orderFlat.splice(0, len));
    if (orderByGroups.length !== order.length)
        throw `Unexpected error in creating an ordering`;
    const nextNodes = actionIdxToIdFlat.map((id) => getNextNodes(actionIdxToIdFlat, (0, exports.getNonSpecificActionDataFromId)(id, actionDatasFormattedIds, true), actionDatasFormattedIds));
    // Check to ensure that there are the appropriate # of actions hit in the ordering
    if (orderByGroups.reduce((prev, group) => prev + group.length, 0) !==
        actionIdxToIdFlat.length)
        throw "Expected the action grouping length to equal the number of ids";
    // Ensure that all action idxs are "found" elements
    orderByGroups.forEach((group) => {
        group.forEach((idx) => {
            if (idx === -1)
                throw `An error occurred with an unexpected next action name`;
        });
    });
    // add number of input mints to all the initial in degrees to account for the initial inputs
    // this means that they get "hit" once for each init token
    initActionIdxsFlat.forEach((idx) => {
        inDegreesFlat[idx] += numberOfInputTokens;
    });
    return {
        order: orderByGroups,
        initActionIdxs: initActionIdxsFlat,
        actionIdxToId: actionIdxToIdFlat,
        inDegrees: inDegreesFlat,
        nextNodes,
    };
};
exports.buildSeqListOfActionCalls = buildSeqListOfActionCalls;
const getNextNodes = (idxToActionId, action, actionDatas) => {
    const getInnerNextNode = (innerNextNode) => {
        const nexts = Object.keys(innerNextNode);
        return nexts.map((id) => {
            return {
                actionIdx: getIndexOfWithGroups(id, idxToActionId, actionDatas),
                fraction: innerNextNode[id],
            };
        });
    };
    return action.nextActions.map(getInnerNextNode);
};
const flattenOrder = (order, actionIdxToInnerId, actionIdxToOuterId, initActionIdxs, inDegrees, actionDatasFormatted) => {
    const actionIdxToIdFlat = actionIdxToInnerId.flat();
    const newOrder = order
        .map((accessIdx) => {
        if (accessIdxIsGroup(accessIdx)) {
            const ids = accessIdx[1].map((innerIdx) => actionIdxToInnerId[accessIdx[0]][innerIdx]);
            const outerId = actionIdxToOuterId[accessIdx[0]];
            return ids.map((innerId) => actionIdxToIdFlat.indexOf(innerId));
        }
        else {
            const outerId = actionIdxToOuterId[accessIdx[0]];
            const idx = getIndexOfWithGroups(outerId, actionIdxToIdFlat, actionDatasFormatted);
            return [idx];
        }
    })
        .flat();
    const initActionIdxsFlat = initActionIdxs.map((i) => {
        // find the access info corresponding to the access info which corresponds
        // to the index in the init action list
        const accessIdx = order.find((o) => o[0] === i);
        if (accessIdxIsGroup(accessIdx)) {
            const outerId = actionIdxToOuterId[accessIdx[0]];
            // Get the first element called in the group
            const innerActionIdx = accessIdx[1][0];
            const innerId = actionIdxToInnerId[accessIdx[0]][innerActionIdx];
            return actionIdxToIdFlat.indexOf(innerId);
        }
        else {
            const outerId = actionIdxToOuterId[accessIdx[0]];
            return actionIdxToIdFlat.indexOf(outerId);
        }
    });
    return {
        orderFlat: newOrder,
        actionIdxToIdFlat,
        initActionIdxsFlat,
        inDegreesFlat: inDegrees.flat(),
    };
};
const accessIdxIsGroup = (a) => a[1].length !== 0;
const ACTION_DELIMITER = "!@#@!";
const createInnerActionId = (actionGroupId, actionId) => `${actionGroupId}${ACTION_DELIMITER}${actionId}`;
exports.createInnerActionId = createInnerActionId;
const prettyPrintActionId = (id) => {
    if (id.includes(ACTION_DELIMITER)) {
        return `group: ${id.replace(ACTION_DELIMITER, ", inner-action: ")}`;
    }
    return `${id}`;
};
exports.prettyPrintActionId = prettyPrintActionId;
const splitInnerActionId = (id) => id.split(ACTION_DELIMITER);
const getNonSpecificActionDataFromId = (id, actionDatas, actionDatasIdsFormatted) => {
    const split = id.split(ACTION_DELIMITER);
    if (split.length === 1) {
        return actionDatas[split[0]].action;
    }
    else {
        return (0, interfaces_1.castToActionGroup)(actionDatas[split[0]]).actions[actionDatasIdsFormatted ? id : split[1]];
    }
};
exports.getNonSpecificActionDataFromId = getNonSpecificActionDataFromId;
const updateNonSpecificActionDataFromId = (id, construction, update, actionDatasIdsFormatted) => {
    const split = id.split(ACTION_DELIMITER);
    if (split.length === 1) {
        construction.actionDatas[split[0]].action = update;
    }
    else {
        (0, interfaces_1.castToActionGroup)(construction.actionDatas[split[0]]).actions[actionDatasIdsFormatted ? id : split[1]] = update;
    }
};
exports.updateNonSpecificActionDataFromId = updateNonSpecificActionDataFromId;
const getIndexOfWithGroups = (id, idxToActionId, actionDatasFormatted) => {
    let idx = idxToActionId.indexOf(id);
    if (idx === -1) {
        // If the next id is a non specific action, get the id of the first action in the group
        if ((0, interfaces_1.isNonSpecificActionGroup)(actionDatasFormatted[id])) {
            const group = (0, interfaces_1.castToActionGroup)(actionDatasFormatted[id]);
            idx = idxToActionId.indexOf(group.first);
        }
        if (idx === -1)
            throw `Could not find next action of id ${id}`;
    }
    return idx;
};
